import React, { useState } from "react";
import { Alert, Button, Divider, List, Modal, Space, Tag, Tooltip, Typography } from "antd";
import { DeleteOutlined, KeyOutlined, MobileOutlined, PlusOutlined, SafetyOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { deleteMfaFactor, deletePasskey, fetchMfaFactors, regenerateRecoveryCodes } from "../../../api/auth/mfaApi";
import { resendVerificationEmail } from "../../../api/auth/authApi";
import { authFailure, authFailureMessage, isStepUpDeclined } from "../../../lib/http/authErrors";
import { requestStepUp } from "../../../lib/http/stepup";
import { hasStrongAmr, useAuthSession } from "../../../lib/http/session";
import { isPasskeySupported } from "../../../lib/webauthn/webauthn";
import { SectionCard, InlineLoading } from "../../../components/shared";
import { useNotifications } from "../../../context/NotificationContext";
import TotpSetup from "./TotpSetup";
import PasskeySetup from "./PasskeySetup";
import SmsRecoverySetup from "./SmsRecoverySetup";
import RecoveryCodesList from "./RecoveryCodesList";
import PasswordLoginSetting from "./PasswordLoginSetting";
import {
	MFA_FACTORS_QUERY_KEY,
	needsEnrolmentEmailCode,
	recoveryPhone,
	refreshSecurityState,
	strongFactorCount,
	totpFactors,
} from "./securityModel";
import "./Security.css";

const { Text, Title, Paragraph } = Typography;

type Dialog = { kind: "totp" } | { kind: "passkey" } | { kind: "sms" } | { kind: "codes"; codes: string[] } | null;

const DIALOG_TITLES: Record<Exclude<Dialog, null>["kind"], string> = {
	totp: "Set up an authenticator app",
	passkey: "Add a passkey",
	sms: "Recovery phone",
	codes: "New recovery codes",
};

const when = (value: string | null): string => (value ? dayjs(value).format("D MMM YYYY") : "never");

const removalError = (error: unknown): string => {
	const failure = authFailure(error);
	if (failure.code === "conflict" && failure.details.reason === "password_login_disabled") {
		return "Turn password sign-in back on before removing your last authenticator app or passkey.";
	}
	return authFailureMessage(error);
};

// The account's sign-in methods and recovery options (contract §6.6–§6.8), for every signed-in user; staff must keep one strong factor.
const SecuritySettings: React.FC = () => {
	const { info } = useAuthSession();
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useNotifications();
	const [dialog, setDialog] = useState<Dialog>(null);
	const [resending, setResending] = useState(false);
	const [resendMessage, setResendMessage] = useState("");
	const factorsQuery = useQuery({ queryKey: MFA_FACTORS_QUERY_KEY, queryFn: fetchMfaFactors });
	const data = factorsQuery.data;
	const totp = totpFactors(data);
	const passkeys = data?.passkeys ?? [];
	const phone = recoveryPhone(data);
	const strongCount = strongFactorCount(data);
	const mfa = info?.mfa;
	const requireEmailCode = needsEnrolmentEmailCode(info, strongCount);
	const passkeysAvailable = isPasskeySupported();
	// Enrolled during grace or after a role change, but this session has not yet proved the factor (contract §6.4).
	const awaitingConfirmation = Boolean(mfa?.required && strongCount > 0 && !hasStrongAmr(info));

	const refresh = () => refreshSecurityState(queryClient);
	// Only an explicit false: a session read before verification existed carries no flag.
	const emailUnverified = info?.user?.email_verified === false;

	const resendVerification = async () => {
		setResending(true);
		try {
			setResendMessage((await resendVerificationEmail()).message);
		} catch (err) {
			showError(authFailureMessage(err));
		} finally {
			setResending(false);
		}
	};

	const closeDialog = () => {
		setDialog(null);
		void refresh();
	};

	const confirmRemoval = (title: string, content: string, remove: () => Promise<void>) =>
		Modal.confirm({
			title,
			content,
			okText: "Remove",
			okType: "danger",
			onOk: async () => {
				try {
					await remove();
					showSuccess("Removed");
				} catch (err) {
					if (!isStepUpDeclined(err)) showError(removalError(err));
				} finally {
					await refresh();
				}
			},
		});

	const regenerateCodes = () =>
		Modal.confirm({
			title: "Create new recovery codes?",
			content: "Your current recovery codes will stop working.",
			okText: "Create new codes",
			onOk: async () => {
				try {
					const { recovery_codes } = await regenerateRecoveryCodes();
					setDialog({ kind: "codes", codes: recovery_codes });
				} catch (err) {
					if (!isStepUpDeclined(err)) showError(authFailureMessage(err));
				}
			},
		});

	// From the factor list, not the session summary, which lags behind an enrolment until the next session read.
	const enrolledMethods = [...(totp.length > 0 ? ["totp"] : []), ...(passkeys.length > 0 ? ["passkey"] : [])];

	const confirmIdentity = async () => {
		if (await requestStepUp({ methods: enrolledMethods })) await refresh();
	};

	const statusBanner = () => {
		if (!mfa) return null;
		if (awaitingConfirmation) {
			return (
				<Alert
					data-testid="security-status-confirm"
					type="warning"
					showIcon
					message="Confirm your new sign-in method"
					description="Confirm with your authenticator app or passkey to finish. Until you do, this session will be paused at its next refresh."
					action={
						<Button size="small" type="primary" onClick={confirmIdentity}>
							Confirm
						</Button>
					}
				/>
			);
		}
		if (mfa.state === "grace") {
			return (
				<Alert
					data-testid="security-status-grace"
					type="warning"
					showIcon
					message="Set up two-step verification"
					description={`Staff accounts need an authenticator app or a passkey${mfa.grace_until ? ` by ${dayjs(mfa.grace_until).format("D MMM YYYY, h:mm a")}` : ""}.`}
				/>
			);
		}
		if (strongCount === 0 && !mfa.required) {
			return (
				<Alert
					data-testid="security-status-optional"
					type="info"
					showIcon
					message="Protect your account with two-step verification"
					description="Add an authenticator app or a passkey. It is optional for your account."
				/>
			);
		}
		if (strongCount > 0) {
			return <Alert data-testid="security-status-on" type="success" showIcon message="Two-step verification is on" />;
		}
		return null;
	};

	return (
		<SectionCard title="Security" subtitle="How you sign in, and how you get back in if you lose your device">
			<div className="security-settings" data-testid="security-settings">
				{factorsQuery.isLoading ? (
					<InlineLoading minHeight={160} message="Loading your sign-in methods…" />
				) : factorsQuery.isError ? (
					<Alert
						type="error"
						showIcon
						message="Could not load your sign-in methods."
						action={
							<Button size="small" onClick={() => factorsQuery.refetch()}>
								Retry
							</Button>
						}
					/>
				) : (
					<>
						{statusBanner()}

						<Title level={5} className="security-heading">
							Sign-in methods
						</Title>
						<List
							data-testid="security-methods"
							locale={{ emptyText: "No authenticator app or passkey yet" }}
							dataSource={[
								...totp.map((f) => ({ key: f.id, kind: "totp" as const, name: f.name, created: f.created_at, used: f.last_used_at })),
								...passkeys.map((p) => ({ key: p.id, kind: "passkey" as const, name: p.name, created: p.created_at, used: p.last_used_at })),
							]}
							renderItem={(item) => (
								<List.Item
									data-testid={`security-method-${item.key}`}
									actions={[
										<Button
											key="remove"
											type="text"
											size="small"
											danger
											icon={<DeleteOutlined />}
											aria-label={`Remove ${item.name}`}
											data-testid={`security-method-remove-${item.key}`}
											onClick={() =>
												confirmRemoval(
													`Remove ${item.name}?`,
													item.kind === "totp" ? "Codes from this app will stop working." : "This passkey will stop working for this account.",
													() => (item.kind === "totp" ? deleteMfaFactor(item.key) : deletePasskey(item.key))
												)
											}
										/>,
									]}
								>
									<List.Item.Meta
										avatar={item.kind === "totp" ? <SafetyOutlined /> : <KeyOutlined />}
										title={
											<Space size="small" wrap>
												<Text>{item.name}</Text>
												<Tag>{item.kind === "totp" ? "Authenticator app" : "Passkey"}</Tag>
											</Space>
										}
										description={`Added ${when(item.created)} · last used ${when(item.used)}`}
									/>
								</List.Item>
							)}
						/>
						<Space wrap className="security-actions">
							<Button data-testid="security-add-totp" icon={<PlusOutlined />} onClick={() => setDialog({ kind: "totp" })}>
								Authenticator app
							</Button>
							<Tooltip title={passkeysAvailable ? undefined : "This browser cannot create passkeys"}>
								<Button data-testid="security-add-passkey" icon={<PlusOutlined />} disabled={!passkeysAvailable} onClick={() => setDialog({ kind: "passkey" })}>
									Passkey
								</Button>
							</Tooltip>
						</Space>

						<Divider />
						<Title level={5} className="security-heading">
							Recovery
						</Title>
						<Paragraph type="secondary">
							If you lose your authenticator app or passkey, choose “Lost your device?” at sign-in. Text and email codes are used only there, never to sign in.
						</Paragraph>
						<div className="security-row" data-testid="security-recovery-codes-row">
							<div>
								<Text strong>Recovery codes</Text>
								<br />
								<Text type="secondary" data-testid="security-recovery-codes-remaining">
									{data?.recovery_codes_remaining ?? 0} unused
								</Text>
							</div>
							<Button data-testid="security-regenerate-codes" onClick={regenerateCodes} disabled={strongCount === 0}>
								Create new codes
							</Button>
						</div>
						{strongCount > 0 && (data?.recovery_codes_remaining ?? 0) === 0 && (
							<Alert data-testid="security-no-recovery-codes" type="warning" showIcon message="You have no recovery codes left. Create new ones." style={{ marginBottom: "var(--mg-sp-6)" }} />
						)}
						<div className="security-row" data-testid="security-recovery-phone-row">
							<div>
								<Text strong>
									<MobileOutlined /> Recovery phone
								</Text>
								<br />
								<Text type="secondary" data-testid="security-recovery-phone">
									{phone?.masked_destination ?? "Not set"}
								</Text>
							</div>
							<Space wrap>
								<Button data-testid="security-recovery-phone-set" onClick={() => setDialog({ kind: "sms" })}>
									{phone ? "Change" : "Add"}
								</Button>
								{phone && (
									<Button
										data-testid="security-recovery-phone-remove"
										danger
										onClick={() => confirmRemoval("Remove your recovery phone?", "You will not be able to recover your account by text message.", () => deleteMfaFactor(phone.id))}
									>
										Remove
									</Button>
								)}
							</Space>
						</div>
						<div className="security-row" data-testid="security-recovery-email-row">
							<div>
								<Text strong>Recovery email</Text>
								<br />
								<Text type="secondary">A code can also be sent to your account email.</Text>
								{emailUnverified && (
									<>
										<br />
										<Text type="warning" data-testid="security-email-unverified">
											Your email address is not verified yet.
										</Text>
									</>
								)}
							</div>
							{emailUnverified && (
								<Button data-testid="security-email-resend" onClick={resendVerification} loading={resending}>
									Resend verification email
								</Button>
							)}
						</div>
						{resendMessage && <Alert data-testid="security-email-resend-sent" type="info" showIcon message={resendMessage} style={{ marginBottom: "var(--mg-sp-6)" }} />}

						<Divider />
						<Title level={5} className="security-heading">
							Password
						</Title>
						<PasswordLoginSetting enabled={data?.password_login_enabled ?? true} hasTotp={totp.length > 0} hasPasskey={passkeys.length > 0} onChanged={() => void refresh()} />
					</>
				)}
			</div>

			<Modal title={dialog ? DIALOG_TITLES[dialog.kind] : undefined} open={dialog !== null} onCancel={closeDialog} footer={null} maskClosable={false} destroyOnClose width={520}>
				{dialog?.kind === "totp" && <TotpSetup requireEmailCode={requireEmailCode} onDone={closeDialog} onCancel={closeDialog} />}
				{dialog?.kind === "passkey" && <PasskeySetup requireEmailCode={requireEmailCode} onDone={closeDialog} onCancel={closeDialog} />}
				{dialog?.kind === "sms" && <SmsRecoverySetup requireEmailCode={requireEmailCode} onDone={closeDialog} onCancel={closeDialog} />}
				{dialog?.kind === "codes" && (
					<>
						<RecoveryCodesList codes={dialog.codes} />
						<Button type="primary" onClick={closeDialog} style={{ marginTop: "var(--mg-sp-7)" }}>
							Done
						</Button>
					</>
				)}
			</Modal>
		</SectionCard>
	);
};

export default SecuritySettings;
